import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["card", "cardFront", "cardBack", "backContent"]

  static values = {
    cards: Array,
    favorites: Array,
    chapterColor: String,
    initialCardId: String,
    previewMode: Boolean
  }

  connect() {
    console.log("Card Feed Controller connected")

    // Setup keyboard navigation
    this.boundHandleKeyboard = this.handleKeyboard.bind(this)
    document.addEventListener("keydown", this.boundHandleKeyboard)

    // Setup intersection observer for URL updates
    this.setupIntersectionObserver()

    // Scroll to initial card if specified
    if (this.hasInitialCardIdValue && this.initialCardIdValue) {
      this.scrollToCard(this.initialCardIdValue)
    }

    // Update all favorite button states
    this.updateAllFavoriteStates()
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKeyboard)
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  // Setup intersection observer to update URL when scrolling through cards
  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: "-50% 0px -50% 0px",
      threshold: 0
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const cardId = entry.target.dataset.cardId
          this.updateURL(cardId)
        }
      })
    }, options)

    // Observe all cards
    this.cardTargets.forEach(card => {
      this.observer.observe(card)
    })
  }

  // Update URL without page reload for shareable card links
  updateURL(cardId) {
    const url = new URL(window.location)
    url.searchParams.set('card_id', cardId)
    window.history.replaceState({}, '', url)
  }

  // Scroll to a specific card (used for direct links)
  scrollToCard(cardId) {
    setTimeout(() => {
      const card = this.cardTargets.find(c => c.dataset.cardId === cardId)
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' })
        // Flash highlight effect
        card.classList.add('highlight-flash')
        setTimeout(() => card.classList.remove('highlight-flash'), 2000)
      }
    }, 100) // Small delay to ensure DOM is ready
  }

  // Flip card to show back content
  flipCard(event) {
    event.preventDefault()
    event.stopPropagation()

    const card = event.currentTarget.closest('[data-card-feed-target="card"]')
    if (!card) return

    // Check if card has expanded content
    const hasContent = card.dataset.hasExpandedContent === 'true'
    if (!hasContent) return

    // If another card is flipped, unflip it first
    this.unflipAllCards()

    // Get front and back elements
    const cardFront = card.querySelector('[data-card-feed-target="cardFront"]')
    const cardBack = card.querySelector('[data-card-feed-target="cardBack"]')
    const backContent = card.querySelector('[data-card-feed-target="backContent"]')

    if (!cardFront || !cardBack) return

    // Flip the card
    card.dataset.flipped = 'true'
    cardFront.classList.add('hidden')
    cardBack.classList.remove('hidden')

    // Blur all other cards
    this.blurAllOtherCards(card)

    // Expand card if content is longer than standard height
    this.expandCardIfNeeded(card, backContent)

    // Scroll card to center
    setTimeout(() => {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 100)

    // Announce to screen readers
    this.announceFlip(card, true)
  }

  // Unflip card to show front
  unflipCard(event) {
    event.preventDefault()
    event.stopPropagation()

    const card = event.currentTarget.closest('[data-card-feed-target="card"]')
    if (!card) return

    this.unflipSingleCard(card)
  }

  // Unflip a specific card
  unflipSingleCard(card) {
    const cardFront = card.querySelector('[data-card-feed-target="cardFront"]')
    const cardBack = card.querySelector('[data-card-feed-target="cardBack"]')

    if (!cardFront || !cardBack) return

    // Unflip the card
    card.dataset.flipped = 'false'
    cardFront.classList.remove('hidden')
    cardBack.classList.add('hidden')

    // Smoothly restore original height
    const standardHeight = 350
    card.style.height = `${standardHeight}px`
    
    // After transition completes, remove inline height style
    setTimeout(() => {
      card.style.height = ''
      card.style.minHeight = `${standardHeight}px`
    }, 400) // Match CSS transition duration

    // Unblur all cards
    this.unblurAllCards()

    // Announce to screen readers
    this.announceFlip(card, false)
  }

  // Unflip all cards
  unflipAllCards() {
    this.cardTargets.forEach(card => {
      if (card.dataset.flipped === 'true') {
        this.unflipSingleCard(card)
      }
    })
  }

  // Check if any card is currently flipped
  isAnyCardFlipped() {
    return this.cardTargets.some(card => card.dataset.flipped === 'true')
  }

  // Blur all cards except the active one
  blurAllOtherCards(activeCard) {
    this.cardTargets.forEach(card => {
      if (card !== activeCard) {
        card.classList.add('card-blurred')
      }
    })
  }

  // Remove blur from all cards
  unblurAllCards() {
    this.cardTargets.forEach(card => {
      card.classList.remove('card-blurred')
    })
  }

  // Expand card if back content is longer than standard height
  expandCardIfNeeded(card, backContent) {
    if (!backContent) return

    // Get current height
    const currentHeight = card.offsetHeight
    
    // Set explicit height to current value first (for smooth transition)
    card.style.height = `${currentHeight}px`

    // Wait for content to render, then calculate and set new height
    requestAnimationFrame(() => {
      setTimeout(() => {
        const contentHeight = backContent.scrollHeight
        const standardHeight = 350
        const padding = 48 // Top and bottom padding for buttons
        const expandedHeight = Math.max(standardHeight, contentHeight + padding + 48)

        // Set the new height - CSS transition will animate it
        card.style.height = `${expandedHeight}px`
        card.dataset.expandedHeight = expandedHeight
      }, 10)
    })
  }

  // Announce flip state to screen readers
  announceFlip(card, isFlipped) {
    const announcement = isFlipped
      ? 'Card flipped to show details'
      : 'Card flipped back to front'

    // Create or update live region
    let liveRegion = document.getElementById('card-flip-announcer')
    if (!liveRegion) {
      liveRegion = document.createElement('div')
      liveRegion.id = 'card-flip-announcer'
      liveRegion.setAttribute('aria-live', 'polite')
      liveRegion.setAttribute('aria-atomic', 'true')
      liveRegion.className = 'sr-only'
      document.body.appendChild(liveRegion)
    }
    liveRegion.textContent = announcement
  }

  // Toggle favorite status
  toggleFavorite(event) {
    event.stopPropagation() // Prevent card flip

    const btn = event.currentTarget
    const card = btn.closest('[data-card-feed-target="card"]')
    const cardId = card.dataset.cardId

    // Optimistic UI update
    const isFavorited = this.favoritesValue.includes(parseInt(cardId))
    const newFavorites = isFavorited
      ? this.favoritesValue.filter(id => id !== parseInt(cardId))
      : [...this.favoritesValue, parseInt(cardId)]

    this.favoritesValue = newFavorites
    this.updateFavoriteButton(btn, !isFavorited)

    // Send to server
    fetch('/add_to_list', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ card_id: cardId })
    })
    .then(response => response.json())
    .then(data => {
      // Server returns updated favorites list
      if (data.favorites) {
        this.favoritesValue = data.favorites
        this.updateAllFavoriteStates()
      }
    })
    .catch(error => {
      console.error('Error toggling favorite:', error)
      // Revert on error
      this.favoritesValue = isFavorited
        ? [...this.favoritesValue, parseInt(cardId)]
        : this.favoritesValue.filter(id => id !== parseInt(cardId))
      this.updateFavoriteButton(btn, isFavorited)
    })
  }

  // Update a single favorite button
  updateFavoriteButton(btn, isFavorited) {
    const heartIcon = btn.querySelector('img')
    if (heartIcon) {
      heartIcon.src = isFavorited
        ? window.assetPaths.images.heartFilled
        : window.assetPaths.images.heartOutline
    }
    btn.setAttribute('aria-label', isFavorited ? 'Remove from favorites' : 'Add to favorites')
  }

  // Update all favorite button states
  updateAllFavoriteStates() {
    this.cardTargets.forEach(card => {
      const cardId = parseInt(card.dataset.cardId)
      const btn = card.querySelector('[data-action*="toggleFavorite"]')
      if (btn) {
        const isFavorited = this.favoritesValue.includes(cardId)
        this.updateFavoriteButton(btn, isFavorited)
      }
    })
  }

  // Open Add to Collection modal
  openAddToCollectionModal(event) {
    event.preventDefault()
    event.stopPropagation()

    const btn = event.currentTarget
    const cardId = btn.dataset.cardId

    // Find the modal (rendered in application layout)
    const modal = document.querySelector('#addToCollectionModal')
    if (modal) {
      // Set the card ID on the modal
      modal.dataset.cardId = cardId
      // Remove hidden class to show modal
      modal.classList.remove('hidden')
    }
  }

  // Keyboard navigation
  handleKeyboard(event) {
    // Handle ESC to unflip cards
    if (event.key === 'Escape') {
      if (this.isAnyCardFlipped()) {
        event.preventDefault()
        this.unflipAllCards()
        return
      }
    }

    // Only handle if not typing in input
    if (event.target.matches('input, textarea')) return

    const currentCard = this.getCurrentVisibleCard()

    switch(event.key.toLowerCase()) {
      case 'f':
        // Toggle favorite on current visible card
        event.preventDefault()
        if (currentCard) {
          const favoriteBtn = currentCard.querySelector('[data-action*="toggleFavorite"]')
          if (favoriteBtn) favoriteBtn.click()
        }
        break

      case ' ':
      case 'enter':
        // Toggle flip on current card (only if it has expanded content)
        event.preventDefault()
        if (currentCard && currentCard.dataset.hasExpandedContent === 'true') {
          const isFlipped = currentCard.dataset.flipped === 'true'
          if (isFlipped) {
            this.unflipSingleCard(currentCard)
          } else {
            const flipBtn = currentCard.querySelector('[data-action*="flipCard"]')
            if (flipBtn) flipBtn.click()
          }
        }
        break

      case 'arrowdown':
        // Navigate to next card
        event.preventDefault()
        this.navigateToAdjacentCard(currentCard, 'next')
        break

      case 'arrowup':
        // Navigate to previous card
        event.preventDefault()
        this.navigateToAdjacentCard(currentCard, 'previous')
        break
    }
  }

  // Get currently visible card (center of viewport)
  getCurrentVisibleCard() {
    const viewportCenter = window.innerHeight / 2
    let closestCard = null
    let closestDistance = Infinity

    this.cardTargets.forEach(card => {
      const rect = card.getBoundingClientRect()
      const cardCenter = rect.top + (rect.height / 2)
      const distance = Math.abs(cardCenter - viewportCenter)

      if (distance < closestDistance) {
        closestDistance = distance
        closestCard = card
      }
    })

    return closestCard
  }

  // Navigate to adjacent card
  navigateToAdjacentCard(currentCard, direction) {
    if (!currentCard) return

    const currentIndex = this.cardTargets.indexOf(currentCard)
    const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1

    if (nextIndex >= 0 && nextIndex < this.cardTargets.length) {
      const nextCard = this.cardTargets[nextIndex]
      nextCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  // Handle favorites value change (update UI when favorites change)
  favoritesValueChanged() {
    this.updateAllFavoriteStates()
  }
}
